import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// @ts-ignore - lucide-react types incompatible with TS 4.9.5
import { ArrowLeft, Plus, Edit2, Trash2, Key, UserCheck, UserX } from 'lucide-react';
import { apiService } from '../../services/api';
import { Config } from '../../services/config';

interface ClienteUsuario {
  id: number;
  nome: string;
  email: string;
  ativo: boolean;
  senha_temporaria: boolean;
  ultimo_login: string | null;
  criado_em: string;
}

const ClienteUsuariosAdmin: React.FC = () => {
  const { clienteId } = useParams<{ clienteId: string }>();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState<ClienteUsuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ nome: '', email: '', senha: '' });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchUsuarios = useCallback(async () => {
    try {
      const url = `${Config.API_BASE_URL}/client-dashboard/clientes/${clienteId}/usuarios`;
      const res = await apiService.request<any>(url);
      setUsuarios(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setSaving(true);

    try {
      if (editingId) {
        const updateData: any = { nome: formData.nome };
        if (formData.senha) {
          updateData.senha = formData.senha;
          updateData.senha_temporaria = true;
        }
        await apiService.request<any>(
          `${Config.API_BASE_URL}/client-dashboard/usuarios/${editingId}`,
          { method: 'PUT', body: JSON.stringify(updateData) }
        );
        setSuccessMsg('Usuário atualizado com sucesso!');
      } else {
        const res = await apiService.request<any>(
          `${Config.API_BASE_URL}/client-dashboard/clientes/${clienteId}/usuarios`,
          { method: 'POST', body: JSON.stringify(formData) }
        );
        if (res.linked) {
          setSuccessMsg('Usuário vinculado com sucesso! Este email já estava cadastrado em outra transportadora. As credenciais existentes foram mantidas.');
        } else {
          setSuccessMsg('Usuário criado com sucesso! A senha temporária foi enviada por email.');
        }
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ nome: '', email: '', senha: '' });
      await fetchUsuarios();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    try {
      await apiService.request<any>(
        `${Config.API_BASE_URL}/client-dashboard/usuarios/${id}`,
        { method: 'DELETE' }
      );
      await fetchUsuarios();
    } catch (err) {
      console.error('Erro ao excluir:', err);
    }
  };

  const handleToggleStatus = async (user: ClienteUsuario) => {
    try {
      await apiService.request<any>(
        `${Config.API_BASE_URL}/client-dashboard/usuarios/${user.id}`,
        { method: 'PUT', body: JSON.stringify({ ativo: !user.ativo }) }
      );
      await fetchUsuarios();
    } catch (err) {
      console.error('Erro ao alterar status:', err);
    }
  };

  const handleResetPassword = async (user: ClienteUsuario) => {
    const novaSenha = Math.random().toString(36).slice(-8) + 'A1!';
    try {
      await apiService.request<any>(
        `${Config.API_BASE_URL}/client-dashboard/usuarios/${user.id}`,
        { method: 'PUT', body: JSON.stringify({ senha: novaSenha, senha_temporaria: true }) }
      );
      alert(`Nova senha temporária: ${novaSenha}\n\nEnvie esta senha ao usuário.`);
      await fetchUsuarios();
    } catch (err) {
      console.error('Erro ao redefinir senha:', err);
    }
  };

  const startEdit = (user: ClienteUsuario) => {
    setEditingId(user.id);
    setFormData({ nome: user.nome, email: user.email, senha: '' });
    setShowForm(true);
    setError('');
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/clientes/visualizar/${clienteId}`)}
          style={{
            background: 'none', border: '1px solid #ddd', borderRadius: 8,
            padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <ArrowLeft size={16} />
          Voltar
        </button>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Usuários do Dashboard do Cliente</h1>
      </div>

      {successMsg && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
          padding: '14px 18px', marginBottom: 16, color: '#15803d', fontSize: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <span>{successMsg}</span>
          <button
            onClick={() => setSuccessMsg('')}
            style={{ background: 'none', border: 'none', color: '#15803d', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}
          >
            &times;
          </button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setFormData({ nome: '', email: '', senha: '' }); setError(''); setSuccessMsg(''); }}
          style={{
            background: '#04364F', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            fontWeight: 500
          }}
        >
          <Plus size={16} />
          Criar Novo Usuário
        </button>
      </div>

      {showForm && (
        <div style={{
          background: '#f8f9fa', border: '1px solid #e2e8f0', borderRadius: 12,
          padding: 24, marginBottom: 24
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
            {editingId ? 'Editar Usuário' : 'Criar Novo Usuário'}
          </h3>

          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: 12, marginBottom: 16, color: '#b91c1c', fontSize: 14
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' }}>Nome</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={e => setFormData(d => ({ ...d, nome: e.target.value }))}
                  required
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                    borderRadius: 8, fontSize: 14, boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' }}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(d => ({ ...d, email: e.target.value }))}
                  required={!editingId}
                  disabled={!!editingId}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
                    ...(editingId ? { background: '#f3f4f6', color: '#6b7280' } : {})
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' }}>
                {editingId ? 'Nova Senha (deixe em branco para manter)' : 'Senha Temporária'}
              </label>
              <input
                type="text"
                value={formData.senha}
                onChange={e => setFormData(d => ({ ...d, senha: e.target.value }))}
                required={!editingId}
                minLength={6}
                style={{
                  width: '100%', maxWidth: 400, padding: '10px 12px', border: '1px solid #d1d5db',
                  borderRadius: 8, fontSize: 14, boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  background: '#04364F', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 24px', cursor: 'pointer', fontWeight: 500
                }}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); }}
                style={{
                  background: '#fff', color: '#374151', border: '1px solid #d1d5db',
                  borderRadius: 8, padding: '10px 24px', cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Carregando...</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Nome</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Último Acesso</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                    Nenhum usuário cadastrado
                  </td>
                </tr>
              ) : (
                usuarios.map(user => (
                  <tr key={user.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px', fontSize: 14 }}>{user.nome}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: '#6b7280' }}>{user.email}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>
                      {user.ultimo_login
                        ? new Date(user.ultimo_login).toLocaleString('pt-BR')
                        : 'Nunca acessou'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        display: 'inline-flex', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        background: user.ativo ? '#dcfce7' : '#fef2f2',
                        color: user.ativo ? '#15803d' : '#b91c1c'
                      }}>
                        {user.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      {user.senha_temporaria && (
                        <span style={{
                          display: 'inline-flex', padding: '3px 10px', borderRadius: 6, fontSize: 12,
                          fontWeight: 600, background: '#fef3c7', color: '#92400e', marginLeft: 6
                        }}>
                          Senha temp.
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => startEdit(user)}
                          title="Editar"
                          style={{
                            padding: 6, background: '#eff6ff', border: '1px solid #bfdbfe',
                            borderRadius: 6, cursor: 'pointer', color: '#2563eb'
                          }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleResetPassword(user)}
                          title="Redefinir senha"
                          style={{
                            padding: 6, background: '#fef3c7', border: '1px solid #fde68a',
                            borderRadius: 6, cursor: 'pointer', color: '#92400e'
                          }}
                        >
                          <Key size={14} />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(user)}
                          title={user.ativo ? 'Desativar' : 'Ativar'}
                          style={{
                            padding: 6,
                            background: user.ativo ? '#fef2f2' : '#dcfce7',
                            border: `1px solid ${user.ativo ? '#fecaca' : '#bbf7d0'}`,
                            borderRadius: 6, cursor: 'pointer',
                            color: user.ativo ? '#b91c1c' : '#15803d'
                          }}
                        >
                          {user.ativo ? <UserX size={14} /> : <UserCheck size={14} />}
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          title="Excluir"
                          style={{
                            padding: 6, background: '#fef2f2', border: '1px solid #fecaca',
                            borderRadius: 6, cursor: 'pointer', color: '#b91c1c'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClienteUsuariosAdmin;
